import { useEffect, useState } from 'react';
import { Action, Subject } from '@erp/authz';
import { authzClient } from '@/context/authz/AuthzClient';
import { getAppRouteMeta } from '@/routing/router-static-data';

export function getPermissionKey(action: string, subject: string): string {
  return `${action}:${subject}`;
}

type PermissionCheck = { action: Action; subject: Subject };

/**
 * A minimal route interface for permission extraction.
 * Mirrors the shape used in nav-from-router.ts.
 */
type RouteForPermissions = {
  options?: { staticData?: unknown };
  children?: unknown;
};

function isRouteForPermissions(value: unknown): value is RouteForPermissions {
  return typeof value === 'object' && value !== null;
}

function getChildren(route: RouteForPermissions): RouteForPermissions[] {
  const { children } = route;
  if (!children) return [];

  if (Array.isArray(children)) {
    return children.filter(isRouteForPermissions);
  }

  if (typeof children === 'object') {
    return Object.values(children).filter(isRouteForPermissions);
  }

  return [];
}

function extractUniquePermissions(
  routeTree: RouteForPermissions,
): PermissionCheck[] {
  const permissionMap = new Map<string, PermissionCheck>();

  // fallow-ignore-next-line complexity
  function visit(route: RouteForPermissions) {
    const meta = getAppRouteMeta(route);
    const access = meta?.access;

    const declared = [
      ...(access?.adminOrPermission ? [access.adminOrPermission] : []),
      ...(access?.permissionsAnyOf ?? []),
      ...(access?.permissionsAllOf ?? []),
    ];
    for (const perm of declared) {
      const key = getPermissionKey(perm.action, perm.subject);
      if (!permissionMap.has(key)) {
        permissionMap.set(key, { action: perm.action, subject: perm.subject });
      }
    }

    for (const child of getChildren(route)) {
      visit(child);
    }
  }

  visit(routeTree);
  return Array.from(permissionMap.values());
}

function buildPermissionMap(
  uniquePermissions: PermissionCheck[],
): Map<string, boolean> {
  const permissionMap = new Map<string, boolean>();

  for (const perm of uniquePermissions) {
    const key = getPermissionKey(perm.action, perm.subject);
    const allowed = authzClient.getCachedPermission(perm.action, perm.subject);
    // If not in cache, default to false (no permission)
    permissionMap.set(key, allowed ?? false);
  }

  return permissionMap;
}

function samePermissions(
  a: Map<string, boolean>,
  b: Map<string, boolean>,
): boolean {
  if (a.size !== b.size) return false;
  for (const [key, allowed] of a) {
    if (b.get(key) !== allowed) return false;
  }
  return true;
}

/**
 * Hook to check route permissions from the pre-populated authz cache.
 *
 * The cache is populated on login via initializeFromAbilityRules and pinned
 * against garbage collection (gcTime: Infinity for authz keys, set in the
 * AuthzClient constructor), so this hook reads synchronously without any
 * loading state.
 *
 * The map is rebuilt whenever the authz cache changes (re-seeding when
 * ability rules refresh, clearing on logout), so consumers that mounted
 * before the cache was seeded — or across a permission change — converge on
 * the current values instead of keeping a mount-time snapshot.
 */
export function useRoutePermissions(routeTree: RouteForPermissions): {
  permissions: Map<string, boolean>;
  isLoading: boolean;
  error: Error | null;
} {
  const uniquePermissions = extractUniquePermissions(routeTree);

  const [permissions, setPermissions] = useState(() =>
    buildPermissionMap(uniquePermissions),
  );

  useEffect(() => {
    // Keep the current map unless a permission actually changed: the cache
    // notifies on every authz query's fetch-state change (dozens on a page
    // load), and a new map re-renders the whole sidebar each time.
    const rebuild = () => {
      const next = buildPermissionMap(uniquePermissions);
      setPermissions((prev) => (samePermissions(prev, next) ? prev : next));
    };
    // Rebuild once on subscribe: the cache may have changed between the
    // initial render and this effect (e.g. the useAuth seeding effect).
    rebuild();
    return authzClient.subscribe(rebuild);
  }, [uniquePermissions]);

  return {
    permissions,
    isLoading: false,
    error: null,
  };
}
